import {useCallback, useEffect, useState} from "react";
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography} from "@mui/material";
import useUrlQueryParams from "../../hooks/useUrlQueryParams";
import {approveOrDeclineByToken, getBookingByToken} from '../requests/booking-requests';
import {getErrorMessageForNonStandardAndStandardResponse} from "../../util/NetworkErrorUtil";
import {withSnackbar} from "../../components/hocs/withSnackbar";
import {formatCurrencyValue} from "../../util/PaymentUtils";
import SecurSpaceContainer from "../../components/common/SecurSpaceContainer";
import SupplierDeclineBookingModal
  from "../../components/supplier-bookings-approvals/modals/declineModal/SupplierDeclineBookingModal";
import {useNavigate} from "react-router";
import {toast} from "react-toastify";
import {BookingApprovalLabel, BookingApprovalLocation, BookingApprovalStatusChip, BookingApprovalTable} from "./styles";
import {SecurSpaceReportContainer} from "../../components/common";

const BookingApprovalConfirmation = ({snackbarShowMessage}) => {

    const { action, token } = useUrlQueryParams();
    const [openInitialDialog, setOpenInitialDialog] = useState(false);
    const [openDeclineDialog, setOpenDeclineDialog] = useState(false);
    const [booking, setBooking] = useState();

    const history = useNavigate();

    const actions = {APPROVE: 'APPROVED', DECLINE: 'DECLINED'};

    const handleError = useCallback((error) => snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(error), 'error'), [snackbarShowMessage]);

    const handleCloseDialogs = () => {
        setOpenInitialDialog(false);
        setOpenDeclineDialog(false);
    };

    const handleApproveBooking = () => {
        const payload = {
            status: actions.APPROVE,
            reason: null
        }
        approveOrDeclineByToken(token, payload)
            .then(({body}) => {
                setBooking(body);
                history(`/bookings/supplier-bookings?bookingNumber=${booking.orderNumber}`, {replace: true});
                toast.success(`Successfully approved booking ${booking.orderNumber}`);
                handleCloseDialogs();
            })
            .catch(error => handleError(error))
    };

    const handleDeclineBooking = () => {
            setOpenDeclineDialog(true);
            setOpenInitialDialog(false);
    };

    const handleSubmitDeclineBooking = (reason) => {
        const payload = {
            bookingId: booking.id,
            status: actions.DECLINE,
            reason: reason.reason,
            notes: reason.notes
        }
        approveOrDeclineByToken(token, payload)
            .then(({body}) => {
                setBooking(body);
                history('/');
                toast.success(`Successfully declined booking ${booking.orderNumber}`, {replace: true})
                handleCloseDialogs();
            })
            .catch(error => handleError(error))
    }

    useEffect(() => {
        getBookingByToken(token)
            .then(({body}) => {
                setBooking(body?.booking);
                setOpenInitialDialog(true);
            })
            .catch(error => handleError(error));
    }, [handleError, token]);

    const dialogMessage = action ? `Are you sure you want to ${action} booking ${booking?.orderNumber}?` : `How would you like to proceed with booking ${booking?.orderNumber}?`

    return <SecurSpaceContainer>
            <Dialog open={openInitialDialog} onClose={handleCloseDialogs}>
                <DialogTitle>Confirm Booking Action</DialogTitle>
                <DialogContent>{dialogMessage}</DialogContent>
                <DialogActions>
                    <Button onClick={handleApproveBooking}>Approve</Button>
                    <Button onClick={handleDeclineBooking}>Decline</Button>
                </DialogActions>
            </Dialog>
            <SupplierDeclineBookingModal
                open={openDeclineDialog}
                onSubmit={handleSubmitDeclineBooking}
                onClose={handleCloseDialogs}
                booking={booking}
                snackbarShowMessage={snackbarShowMessage}
            />
        {
            booking ?
                <SecurSpaceReportContainer>
                    <Typography variant="h4">{booking.status}</Typography>
                    <Grid item container>
                        <BookingApprovalLocation variatnt="h3">{booking.location.locationName}</BookingApprovalLocation>
                        <Grid container>
                                <BookingApprovalTable container >
                                    <Grid item>
                                        <BookingApprovalLabel variant='body2'>Customer:</BookingApprovalLabel>
                                        <BookingApprovalLabel variant='body2'>Location:</BookingApprovalLabel>
                                        <BookingApprovalLabel variant='body2'>Booking Number:</BookingApprovalLabel>

                                        <BookingApprovalLabel variant='body2'>Booked Dates:</BookingApprovalLabel>
                                        <BookingApprovalLabel variant='body2'>Spaces Booked:</BookingApprovalLabel>
                                        <BookingApprovalLabel variant='body2'>Frequency:</BookingApprovalLabel>
                                        <BookingApprovalLabel variant='body2'>Brokered:</BookingApprovalLabel>
                                        <BookingApprovalLabel variant='body2'>Rate:</BookingApprovalLabel>
                                        <BookingApprovalLabel variant='body2'>Initial Payout:</BookingApprovalLabel>

                                        {booking.recurringSupplierPayoutAmount ? <BookingApprovalLabel variant='body2'>Recurring Payout:</BookingApprovalLabel> : ''}
                                        <BookingApprovalLabel variant='body2'>{booking.brokeredSupplierOverageRatePaid ? 'Overage Rate Paid:' : "Price Per Day"}</BookingApprovalLabel>
                                        <BookingApprovalLabel variant='body2'>Asset Type:</BookingApprovalLabel>
                                    </Grid>
                                    <Grid item>
                                        <Typography variant='body2'>{booking.buyerAccount.companyName}</Typography>
                                        <Typography variant='body2'>{booking.location.locationName}</Typography>
                                        <Typography variant='body2'>{booking.orderNumber}</Typography>

                                        <Typography variant='body2'>{booking.startDate} to {booking.endDate}
                                        </Typography>
                                        <Typography variant='body2'>{booking.numberOfSpaces}</Typography>
                                        <Typography variant='body2'>{
                                            booking.frequency === 'RECURRING' && booking.durationType === 'WEEKLY' ?
                                                'Recurring - Weekly'
                                                :
                                                booking.frequency === 'RECURRING' && booking.durationType === 'MONTHLY' ?
                                                    'Recurring - Monthly'
                                                    :
                                                    'One-Time'

                                        }</Typography><Typography variant='body2'>{booking.brokered ? "True" : "False"}</Typography>
                                        {booking.brokered ? '' :
                                            <Typography variant='body2'>{formatCurrencyValue(booking.rate)}
                                                {
                                                    booking.durationType === 'WEEKLY' ? ' /week' : booking.durationType === 'MONTHLY' ? ' /month' : ' /day'
                                                }
                                            </Typography>
                                        }
                                        <Typography variant='body2'>{formatCurrencyValue(booking.initialSupplierPayoutAmount)}</Typography>
                                        {booking.frequency === 'RECURRING' ?
                                            <Typography variant='body2'>{formatCurrencyValue(booking.recurringSupplierPayoutAmount)}</Typography> : ''}
                                        {
                                            booking.brokeredSupplierOverageRatePaid ?
                                                <Typography variant='body2'>{formatCurrencyValue(booking.brokeredSupplierOverageRatePaid)}</Typography>
                                                :
                                                <Typography variant='body2'>{formatCurrencyValue(booking.location.pricePerDay)}</Typography>
                                        }

                                        <Typography variant='body2'>{booking.assetType}</Typography>
                                    </Grid>
                                </BookingApprovalTable>

                            <BookingApprovalTable container>

                                <Grid item>
                                    <BookingApprovalStatusChip isApproved={booking.status === 'Approved'}>{booking.status}</BookingApprovalStatusChip>
                                </Grid>
                                {booking.numberOfSpaces <= booking.locationInventoryCount ?
                                    <Typography variant='body2'>Booking at or above capacity!</Typography> : ''}
                            </BookingApprovalTable>

                        </Grid>
                    </Grid>
                </SecurSpaceReportContainer>
            : ''
        }
    </SecurSpaceContainer>
}

export default withSnackbar(BookingApprovalConfirmation);
